import axios, { CancelToken } from 'axios';
import {
  Accessibility,
  AccessibilityFilter,
  ProviderTypesMap,
  SortDirection,
  SearchRadius,
  SortCriteria,
  ProviderTypeScrollPosition,
} from '@apps/shared/src/providerSearch/types/providerSearch';
import p, { ProviderSearchActionTypes } from './types/providerSearchActions';

export function getProviders(
  planID: string,
  selectedZipCode: string,
  selectedRadius: string,
  searchInput: string,
  limit: number,
  page: number,
  accessibilities: Accessibility[],
  providerType = '',
  providerSpeciality = '',
  gender = '',
  entityType = ''
): ProviderSearchActionTypes {
  const params = new URLSearchParams();

  params.append('zip', selectedZipCode);
  params.append('planID', planID);
  params.append('limit', limit.toString());
  params.append('page', page.toString());
  params.append('providerType', providerType);
  params.append('providerSpeciality', providerSpeciality);
  params.append('gender', gender);
  params.append('entityType', entityType);

  if (accessibilities.length) {
    params.append('accessibilities', accessibilities.join(','));
  }

  if (searchInput.trim()) {
    params.append('text', searchInput.trim());
  } else {
    params.append('radius', selectedRadius);
  }

  return {
    type: p.GET_PROVIDERS,
    meta: {
      arg: { selectedZipCode, providerType },
    },
    payload: axios.get(`/api/provider-search/v1?${params.toString()}`, {
      headers: { 'X-CSRF-Token': localStorage.getItem('csrfToken') || '' },
    }),
  };
}

export function setSearchInput(
  planID: string,
  selectedZipCode: string,
  input: string,
  selectedRadius: string,
  accessibilities: Accessibility[],
  providerType = '',
  providerSpeciality = '',
  gender = '',
  entityType = '',
  cancelToken?: CancelToken
): ProviderSearchActionTypes {
  const params = new URLSearchParams();

  params.append('zip', selectedZipCode);
  params.append('planID', planID);
  params.append('limit', '50');
  params.append('page', '1');
  params.append('providerType', providerType);
  params.append('providerSpeciality', providerSpeciality);
  params.append('gender', gender);
  params.append('entityType', entityType);

  if (accessibilities.length) {
    params.append('accessibilities', accessibilities.join(','));
  }

  if (input.trim()) {
    params.append('text', input.trim());
  } else {
    params.append('radius', selectedRadius);
  }

  return {
    type: p.SET_SEARCH_INPUT,
    meta: {
      selectedZipCode,
      providerType,
      searchInput: input,
    },
    payload: axios.get(`/api/provider-search/v1?${params.toString()}`, {
      headers: { 'X-CSRF-Token': localStorage.getItem('csrfToken') },
      cancelToken,
    }),
  };
}

export function setSearchRadius(radius: SearchRadius): ProviderSearchActionTypes {
  return {
    type: p.SET_SEARCH_RADIUS,
    payload: radius,
  };
}

export function setZipCode(zip: string): ProviderSearchActionTypes {
  return {
    type: p.SET_ZIP_CODE,
    payload: zip,
  };
}

export function setProviderType(providerType: string): ProviderSearchActionTypes {
  return {
    type: p.SET_PROVIDER_TYPE,
    payload: providerType,
  };
}

export function setProviderSpecialty(providerSpecialty: string): ProviderSearchActionTypes {
  return {
    type: p.SET_PROVIDER_SPECIALTY,
    payload: providerSpecialty,
  };
}

export function setScrollPosition(
  scrollPosition: ProviderTypeScrollPosition
): ProviderSearchActionTypes {
  return {
    type: p.SET_SCROLL_POSITION,
    payload: scrollPosition,
  };
}

export const setProviderTypesMap = (map: ProviderTypesMap) => ({
  type: p.SET_PROVIDER_TYPES_MAP,
  payload: map,
});

export function setIsFilterTrayOpen(isFilterTrayOpen: boolean): ProviderSearchActionTypes {
  return {
    type: p.SET_IS_FILTER_TRAY_OPEN,
    payload: isFilterTrayOpen,
  };
}

export function toggleAccessibility(accessibility: AccessibilityFilter): ProviderSearchActionTypes {
  return {
    type: p.TOGGLE_ACCESSIBILITY,
    payload: accessibility,
  } as any;
}

export function setAccessibilities(
  accessibilities: AccessibilityFilter[]
): ProviderSearchActionTypes {
  return {
    type: p.SET_ACCESSIBILITIES,
    payload: accessibilities,
  };
}

export function setSortCriteria(sortCriteria: SortCriteria): ProviderSearchActionTypes {
  return {
    type: p.SET_SORT_CRITERIA,
    payload: sortCriteria,
  };
}

export function setSortDirection(sortDirection: SortDirection): ProviderSearchActionTypes {
  return {
    type: p.SET_SORT_DIRECTION,
    payload: sortDirection,
  };
}

export function setGenderFilter(gender: string): ProviderSearchActionTypes {
  return {
    type: p.SET_PROVIDER_GENDER_CODE,
    payload: gender,
  };
}

export function clearSearchInput(): ProviderSearchActionTypes {
  return {
    type: p.RESET_SEARCH_INPUT,
  };
}

export function setEntityType(entityType: string): ProviderSearchActionTypes {
  return {
    type: p.SET_ENTITY_TYPE,
    payload: entityType,
  };
}

export function setSearchClicked(searchClicked: boolean): ProviderSearchActionTypes {
  return {
    type: p.SET_SEARCH_CLICKED,
    payload: searchClicked,
  };
}

export function toggleSortDirection(): ProviderSearchActionTypes {
  return {
    type: p.TOGGLE_SORT_DIRECTION,
  };
}

export function toggleChangePlan(): ProviderSearchActionTypes {
  return {
    type: p.TOGGLE_CHANGE_PLAN,
  };
}
export function resetProviderSearch(): ProviderSearchActionTypes {
  return {
    type: p.RESET_PROVIDER_SEARCH,
  };
}
